/**
 * /owner/receipt-claims/:claimId — Owner Receipt Verification detail
 *
 * Phases:
 *  R1 — image preview + AI extraction + approve/reject
 *  R6 — extended fields (outlet / cashier / order_type / table)
 *  R8 (2026-07-02) — Customer Details section, manual OCR correction
 *      (edit mode), dedup warning banner, correction history, Reopen CTA
 *      for rejected claims whose corrected receipt_no is unique.
 *
 * Hard rules preserved:
 *  * Approve is the ONLY stamp-award path (routes through the sealed
 *    receipt_claims_stamp_adapter.py).
 *  * Correction never mints a stamp. It can only move a `rejected` claim
 *    back to `pending_owner_review`, after which the Owner approves via
 *    the existing endpoint.
 *  * `original_ai_extraction` is preserved on the FIRST correction and
 *    never overwritten by subsequent edits.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, Receipt, Activity, Clock, Hourglass, CheckCircle2,
  XCircle, ShieldCheck, Loader2, AlertTriangle, Pencil, Save, X,
  Undo2, History, User, IdCard, Mail, Phone, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

import { api, API_BASE } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, StatusPill,
} from '@/components/ui_primitives/Misc';
import {
  formatRupiah, claimStatusLabel, rejectionReasonLabel,
  REJECTION_REASONS,
} from '@/lib/claimErrorUI';
import DedupFlagBanner from '@/components/owner/DedupFlagBanner';
import DuplicateComparisonPanel from '@/components/owner/DuplicateComparisonPanel';
import DuplicateDecisionButtons, { DecidedStrip } from '@/components/owner/DuplicateDecisionButtons';
import BranchTagDropdown from '@/components/owner/BranchTagDropdown';
import UndoConfirmDuplicateButton from '@/components/owner/UndoConfirmDuplicateButton';

const ORDER_TYPE_CHOICES = [
  { value: '',          label: '— (none)' },
  { value: 'DINE_IN',   label: 'DINE_IN'  },
  { value: 'TAKEAWAY',  label: 'TAKEAWAY' },
];

export default function OwnerReceiptClaimDetailPage() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [imageSrc, setImageSrc] = useState(null);
  // Phase R10.7b (2026-07-05) — hotfix for stuck "Loading image…":
  //   * imageError    — populated when the authenticated image fetch fails
  //                     ({ status, code, message }).
  //   * imageRetryKey — bumped by the Retry button to re-trigger the effect.
  const [imageError, setImageError] = useState(null);
  const [imageRetryKey, setImageRetryKey] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Phase R10.4 (2026-07-05) — scroll anchor so the banner's
  // "View Comparison" link can jump to the DuplicateComparisonPanel.
  const comparisonRef = useRef(null);

  const claimQ = useQuery({
    queryKey: qk.ownerReceiptClaim(claimId),
    queryFn: () => api.ownerGetReceiptClaim(token, claimId),
    enabled: !!token && !!claimId,
    refetchInterval: (data) =>
      data?.status === 'pending_ai_analysis' ? 2500 : false,
  });
  const claim = claimQ.data;

  // Image: fetch via Bearer auth and swap in as a blob URL.
  // Phase R10.7b (2026-07-05) — surface fetch failures so the UI stops
  // spinning forever on 404 (file missing on disk), 401/403 (auth/RBAC),
  // or network errors. Operator spec: unified "Image unavailable" message
  // with specific reason as secondary line + Retry affordance.
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    (async () => {
      if (!claim?.image_url || !token) return;
      // Reset state when we (re)fetch so the retry button clears the error.
      setImageError(null);
      setImageSrc(null);
      try {
        const res = await fetch(`${API_BASE}${claim.image_url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          let code = null;
          let message = null;
          try {
            const body = await res.json();
            code = body?.detail?.code ?? null;
            message = body?.detail?.message ?? null;
          } catch { /* body wasn't JSON */ }
          if (!cancelled) {
            setImageError({ status: res.status, code, message });
          }
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setImageSrc(url);
      } catch {
        if (!cancelled) {
          setImageError({
            status: 0,
            code: 'network_error',
            message: 'Network error — check your connection.',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [claim?.image_url, token, imageRetryKey]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: qk.ownerReceiptClaim(claimId) });
    queryClient.invalidateQueries({ queryKey: ['owner-receipt-claims'] });
    queryClient.invalidateQueries({ queryKey: qk.ownerReceiptClaimsPendingCount() });
  };

  const approveMut = useMutation({
    mutationFn: () => api.ownerApproveReceiptClaim(token, claimId),
    onSuccess: (res) => {
      // Phase R9 (2026-07-03) — direct-approve-from-rejected feedback.
      if (res?.from_rejected) {
        toast.success('Rejection overridden · +1 stamp awarded', {
          description: res?.first_visit_auto_join
            ? 'First visit — customer card was auto-created.'
            : res?.previous_rejection_reason
              ? `Previously rejected: ${res.previous_rejection_reason}`
              : undefined,
        });
      } else {
        toast.success('Claim approved · +1 stamp awarded', {
          description: res?.first_visit_auto_join
            ? 'First visit — customer card was auto-created.'
            : undefined,
        });
      }
      invalidateAll();
    },
    onError: (e) => {
      // Phase R9 — surface the 409 dedup codes with a persistent toast
      // including a jump link to the matching claim.
      // Phase R9.1 (2026-07-03) — surface the new 400 missing-dedup-fields
      // code with a persistent banner telling the Owner to use Correct OCR.
      //
      // Note: FastAPI wraps our structured error body under `detail`, so
      // the ORIGINAL dict lives on `err.raw.detail` (not `err.detail`,
      // which is the human-formatted string produced by `formatError`).
      const structured = (e?.raw && typeof e.raw === 'object' && e.raw.detail && typeof e.raw.detail === 'object')
        ? e.raw.detail
        : {};
      if (structured.error_code === 'missing_dedup_fields') {
        const human = formatMissingFields(structured.missing_fields);
        toast.error('Cannot approve · missing required fields', {
          description:
            `Receipt is missing ${human}. Please use 'Correct OCR' to supply ` +
            `the required fields first.`,
          duration: 10000,
        });
        // Nudge the Owner toward the fix path.
        if (isCorrectable && !editMode) {
          beginEdit();
        }
        return;
      }
      if (structured.code === 'duplicate_value_date' && structured.matching_claim_id) {
        toast.error('Approval blocked · duplicate value+date', {
          description: structured.message,
          action: {
            label: 'View matching claim',
            onClick: () =>
              navigate(`/owner/receipt-claims/${structured.matching_claim_id}`),
          },
          duration: 10000,
        });
        return;
      }
      if (structured.code === 'duplicate_receipt' && structured.matching_claim_id) {
        toast.error('Approval blocked · duplicate receipt number', {
          description: structured.message,
          action: {
            label: 'View matching claim',
            onClick: () =>
              navigate(`/owner/receipt-claims/${structured.matching_claim_id}`),
          },
          duration: 10000,
        });
        return;
      }
      toast.error(e?.detail ?? 'Approve failed');
    },
  });

  const rejectMut = useMutation({
    mutationFn: (payload) => api.ownerRejectReceiptClaim(token, claimId, payload),
    onSuccess: () => {
      toast.success('Claim rejected');
      setRejectOpen(false);
      invalidateAll();
    },
    onError: (e) => toast.error(e?.detail?.message ?? e?.detail ?? 'Reject failed'),
  });

  const correctMut = useMutation({
    mutationFn: (corrections) =>
      api.ownerCorrectReceiptClaim(token, claimId, corrections),
    onSuccess: (res) => {
      setEditMode(false);
      setEditValues(null);
      if (res?.correction_noop) {
        toast.info('No fields changed');
      } else if (res?.dup_match) {
        toast.error('Correction saved · receipt matches an approved claim', {
          description: `Matching claim: ${res.dup_match.id.slice(0, 8)}…`,
        });
      } else if (res?.can_reopen) {
        toast.success('Correction saved · receipt is unique', {
          description: 'You can now reopen this claim for review.',
        });
      } else {
        toast.success('Correction saved');
      }
      invalidateAll();
    },
    onError: (e) => toast.error(e?.detail?.message ?? e?.detail ?? 'Save failed'),
  });

  const reopenMut = useMutation({
    mutationFn: () => api.ownerReopenReceiptClaim(token, claimId),
    onSuccess: () => {
      toast.success('Claim reopened for review');
      invalidateAll();
    },
    onError: (e) => toast.error(e?.detail?.message ?? e?.detail ?? 'Reopen failed'),
  });

  // ---- Phase R10.4 (2026-07-05) — flag-decision + branch-tag mutations.
  const confirmDifferentMut = useMutation({
    mutationFn: (reason) =>
      api.ownerConfirmDifferentTransaction(token, claimId, reason),
    onSuccess: () => {
      toast.success('Flag cleared · claim is now ready for approval review.');
      invalidateAll();
    },
    onError: (e) =>
      toast.error(e?.detail?.message ?? e?.detail ?? 'Could not clear the duplicate flag'),
  });

  const confirmDuplicateMut = useMutation({
    mutationFn: (reason) => api.ownerConfirmDuplicate(token, claimId, reason),
    onSuccess: () => {
      toast.success('Claim rejected as duplicate.');
      invalidateAll();
    },
    onError: (e) =>
      toast.error(e?.detail?.message ?? e?.detail ?? 'Could not confirm duplicate'),
  });

  // Branches for the owner's brand (needed by BranchTagDropdown +
  // the DuplicateComparisonPanel to render branch names).
  const branchesQ = useQuery({
    queryKey: ['owner-branches-detail'],
    queryFn: () => api.ownerListBranches(token),
    enabled: !!token,
    staleTime: 60_000,
  });
  const branches = Array.isArray(branchesQ.data) ? branchesQ.data : [];

  const branchTagMut = useMutation({
    mutationFn: (nextId) => api.ownerTagBranch(token, claimId, nextId),
    onSuccess: (res) => {
      const nextId = res?.branch_id ?? null;
      const label = nextId
        ? (branches.find((b) => b.id === nextId)?.name ?? 'Branch')
        : 'Unassigned';
      toast.success(`Branch updated to ${label}`);
      invalidateAll();
    },
    onError: (e) =>
      toast.error(e?.detail?.message ?? e?.detail ?? 'Branch tag failed'),
  });

  // R10.4b (2026-07-05) — undo-confirm-duplicate mutation.
  // Note: the modal's inline-error handling needs the mutation to
  // throw on failure, so we re-throw inside `mutateAsync` consumers
  // (handled by the button's try/catch — see UndoConfirmDuplicateButton).
  const undoConfirmDuplicateMut = useMutation({
    mutationFn: (reason) => api.ownerUndoConfirmDuplicate(token, claimId, reason),
    onSuccess: () => {
      toast.success('Decision undone. Claim is back in Manual Review.');
      invalidateAll();
    },
    onError: (e) => {
      // Toast fires alongside the inline modal error for parity with
      // other mutation flows on this page.
      toast.error(e?.detail?.message ?? e?.detail ?? 'Could not undo the decision');
    },
  });

  const ai = claim?.ai_extraction;
  const isCorrectableRole = user?.role === 'brand_owner' || user?.role === 'super_admin';
  const isCorrectable =
    isCorrectableRole &&
    (claim?.status === 'rejected' || claim?.status === 'pending_owner_review');
  const isAnalyzing = claim?.status === 'pending_ai_analysis';
  const isDecided = claim?.status === 'approved' || claim?.status === 'rejected';
  // Phase R9 (2026-07-03) — Approve CTA is now also valid on `rejected`
  // claims so an Owner can override an auto-rejection without the
  // Correct OCR → Reopen → Approve dance. Reject stays locked to
  // `pending_owner_review` (a rejected claim is already rejected).
  const canApproveNow =
    isCorrectableRole && !isAnalyzing && !editMode && (
      claim?.status === 'pending_owner_review' ||
      claim?.status === 'rejected'
    );
  const canRejectNow =
    isCorrectableRole && !isAnalyzing && !editMode &&
    claim?.status === 'pending_owner_review';
  const approveFromRejected = canApproveNow && claim?.status === 'rejected';

  // ---- Phase R10.4 (2026-07-05) — dedup-flag-aware Approve state (AC5).
  // Approve stays visible but gets disabled with a tooltip reason when
  // the flag or decision blocks it. Owner always sees WHY.
  const dedupFlagKey = (claim?.dedup_flag || 'green').toLowerCase();
  const dedupMatches = Array.isArray(claim?.dedup_matching_claim_ids)
    ? claim.dedup_matching_claim_ids.filter(Boolean)
    : [];
  const ownerDupDecision = claim?.owner_duplicate_decision ?? null;
  const dedupBlocksApprove =
    (dedupFlagKey === 'orange' && ownerDupDecision !== 'confirmed_different') ||
    dedupFlagKey === 'red';
  const approveTooltip = dedupBlocksApprove
    ? (dedupFlagKey === 'red'
        ? 'Blocked: confirmed duplicate. Reopen the claim via R8 Reopen first if this was a mistake.'
        : "Resolve duplicate flag first — click 'Confirm Different Transaction' or 'Confirm Duplicate' above.")
    : (dedupFlagKey === 'yellow'
        ? 'This claim has unverified OCR fields — review before approving.'
        : null);

  const scrollToComparison = () => {
    comparisonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Enter edit mode — snapshot the current AI values into local state.
  const beginEdit = () => {
    if (!ai) return;
    setEditValues({
      brand_name:   ai.brand_name   ?? '',
      outlet:       ai.outlet       ?? '',
      cashier:      ai.cashier      ?? '',
      order_type:   ai.order_type   ?? '',
      table:        ai.table        ?? '',
      receipt_no:   ai.receipt_no   ?? '',
      date:         ai.date         ?? '',
      total_amount: ai.total_amount != null ? String(ai.total_amount) : '',
      items_text:   serializeItems(ai.items),
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditValues(null);
  };

  const saveEdit = () => {
    const corrections = buildCorrectionPayload(ai, editValues);
    if (Object.keys(corrections).length === 0) {
      toast.info('No fields changed');
      cancelEdit();
      return;
    }
    correctMut.mutate(corrections);
  };

  if (claimQ.isLoading && !claim) return <FullSpinner />;
  if (claimQ.error) {
    return <QueryError error={claimQ.error} onRetry={() => claimQ.refetch()} />;
  }
  if (!claim) {
    return (
      <div className="text-center py-24 text-white/55">
        <div className="text-base font-medium mb-1">Claim not found</div>
        <button
          type="button"
          onClick={() => navigate('/owner/receipt-claims')}
          className="text-gold text-sm hover:underline"
        >
          Back to receipt claims
        </button>
      </div>
    );
  }

  const confidencePct = ai?.overall_confidence != null
    ? Math.round(ai.overall_confidence * 100)
    : null;
  const brandConfPct = ai?.brand_match_confidence != null
    ? Math.round(ai.brand_match_confidence * 100)
    : null;
  const items = ai?.items ?? [];

  return (
    <div className="space-y-6" data-testid="owner-receipt-claim-detail">
      <button
        type="button"
        onClick={() => navigate('/owner/receipt-claims')}
        className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white"
        data-testid="owner-receipt-claim-back"
      >
        <ChevronLeft size={14} /> All claims
      </button>

      <PageHeader
        kicker="Receipt Claim"
        title={ai?.brand_name ?? 'Receipt claim detail'}
        subtitle={`Submitted ${new Date(claim.created_at).toLocaleString('id-ID', {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        })} · Customer ${claim.customer_user_id?.slice(0, 8) ?? '—'}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Phase R10.4 — branch dropdown lives next to the status pill. */}
            {isCorrectableRole ? (
              <div className="flex flex-col items-end gap-1">
                <BranchTagDropdown
                  claim={claim}
                  branches={branches}
                  loading={branchesQ.isLoading}
                  pending={branchTagMut.isPending}
                  onTag={(nextId) => branchTagMut.mutate(nextId)}
                />
                {/* Phase R10.4c (2026-07-05) — surface branch_assignment_source
                    as a quiet info strip so the owner understands WHY the
                    dropdown reads as it does. */}
                {claim?.branch_assignment_source === 'outlet_mapping'
                 && claim?.branch_id ? (
                  <span
                    className="text-[10px] text-gold/80 whitespace-nowrap"
                    data-testid="owner-receipt-claim-branch-source-outlet-mapping"
                  >
                    ⚡ Auto-resolved from mapping: {claim?.ai_extraction?.outlet || '—'} → {branches.find((b) => b.id === claim.branch_id)?.name ?? '—'}
                  </span>
                ) : claim?.branch_assignment_source === 'outlet_mapping_backfill'
                 && claim?.branch_id ? (
                  <span
                    className="text-[10px] text-gold/80 whitespace-nowrap"
                    data-testid="owner-receipt-claim-branch-source-outlet-mapping-backfill"
                  >
                    ⚡ Auto-resolved by owner backfill: {claim?.ai_extraction?.outlet || '—'} → {branches.find((b) => b.id === claim.branch_id)?.name ?? '—'}
                  </span>
                ) : claim?.branch_assignment_source === 'manual_owner' ? (
                  <span
                    className="text-[10px] text-white/55 whitespace-nowrap"
                    data-testid="owner-receipt-claim-branch-source-manual-owner"
                  >
                    ✓ Branch manually assigned by owner
                  </span>
                ) : null}
              </div>
            ) : null}
            <HeaderStatus status={claim.status} />
          </div>
        }
      />

      {/* Phase R10.4 — dedup flag banner (hidden on green). */}
      <DedupFlagBanner
        flag={claim?.dedup_flag}
        reason={claim?.dedup_flag_reason}
        matchingClaimCount={dedupMatches.length}
        onScrollToComparison={scrollToComparison}
      />

      {/* Phase R10.4b (2026-07-05) — "Confirmed as ..." decided strip
          lifted OUT of the dedupMatches-gated block so it stays visible
          after a successful Confirm Different (which flips the flag back
          to green and clears matching_claim_ids). Also hosts the time-
          boxed Undo Confirm Duplicate affordance for recently-rejected
          confirmed_duplicate claims. */}
      {ownerDupDecision === 'confirmed_different' ||
       ownerDupDecision === 'confirmed_duplicate' ? (
        <div className="space-y-2" data-testid="owner-decision-strip-wrap">
          <DecidedStrip claim={claim} decision={ownerDupDecision} />
          {ownerDupDecision === 'confirmed_duplicate' && isCorrectableRole ? (
            <UndoConfirmDuplicateButton
              claim={claim}
              pending={undoConfirmDuplicateMut.isPending}
              onUndo={(reason) => undoConfirmDuplicateMut.mutateAsync(reason)}
            />
          ) : null}
        </div>
      ) : null}

      {/* AI banner — surfaces NON-NEGOTIABLE: AI never auto-awards */}
      {!isDecided ? (
        <div
          className="flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/5 p-4"
          data-testid="owner-receipt-claim-ai-notice"
        >
          <ShieldCheck size={18} className="text-gold mt-0.5 shrink-0" />
          <div className="text-sm text-white/80 leading-relaxed">
            <span className="font-semibold text-gold">
              AI extracted these details but did not award a stamp.
            </span>{' '}
            Only your explicit approval mints a stamp. Verify the brand,
            total, and receipt number before approving.
          </div>
        </div>
      ) : null}

      {/* R8 — Customer Details */}
      <CustomerDetailsCard customer={claim.customer} customerUserId={claim.customer_user_id} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Image column */}
        <SectionCard
          padded={false}
          className="lg:col-span-2 overflow-hidden"
          testID="owner-receipt-claim-image"
        >
          <div className="aspect-[3/4] bg-black grid place-items-center">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt="Receipt"
                className="w-full h-full object-contain bg-[#0A0A0A]"
              />
            ) : imageError ? (
              <div
                className="flex flex-col items-center gap-2 text-xs p-4 text-center max-w-[85%]"
                data-testid="owner-receipt-claim-image-error"
              >
                <AlertTriangle size={24} className="text-amber-300" />
                <div className="text-sm font-semibold text-white/90">
                  Image unavailable
                </div>
                <div
                  className="text-white/55 leading-relaxed"
                  data-testid="owner-receipt-claim-image-error-reason"
                >
                  {humanizeImageError(imageError)}
                </div>
                <button
                  type="button"
                  onClick={() => setImageRetryKey((k) => k + 1)}
                  data-testid="owner-receipt-claim-image-retry"
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 hover:border-gold/60 hover:text-gold text-white/85 text-[11px] font-semibold transition-colors"
                >
                  <RotateCcw size={11} />
                  Retry
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-white/40 text-xs">
                <Loader2 size={24} className="animate-spin" />
                Loading image…
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between gap-3 text-xs text-white/55">
            <span className="font-mono">
              {Math.round((claim.image_size_bytes ?? 0) / 1024)} KB
            </span>
            <span className="font-mono truncate">
              {claim.image_mime ?? '—'}
            </span>
          </div>
        </SectionCard>

        {/* Extraction + actions column */}
        <div className="lg:col-span-3 space-y-5">
          {isAnalyzing ? (
            <SectionCard testID="owner-receipt-claim-analyzing">
              <div className="flex items-center gap-3">
                <Loader2 size={20} className="text-gold animate-spin" />
                <div>
                  <div className="text-sm font-semibold text-white">
                    AI is still analyzing this receipt
                  </div>
                  <div className="text-xs text-white/55 mt-0.5">
                    Hang on — this usually takes a few seconds. The page will
                    refresh automatically.
                  </div>
                </div>
              </div>
            </SectionCard>
          ) : (
            <SectionCard testID="owner-receipt-claim-ai-panel">
              <div className="flex items-start justify-between gap-3 mb-4">
                <h3 className="font-display text-lg font-semibold">
                  AI Extraction
                </h3>
                <div className="flex flex-wrap items-center gap-2 shrink-0 justify-end">
                  {confidencePct != null ? (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/15 border border-gold/35 text-gold text-[11px] font-semibold"
                      data-testid="owner-receipt-claim-confidence"
                    >
                      <Activity size={11} />
                      Overall {confidencePct}%
                    </span>
                  ) : null}
                  {brandConfPct != null ? (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 border border-white/15 text-white/80 text-[11px] font-semibold"
                      data-testid="owner-receipt-claim-brand-confidence"
                    >
                      Brand {brandConfPct}%
                    </span>
                  ) : null}
                  {!editMode && isCorrectable ? (
                    <button
                      type="button"
                      onClick={beginEdit}
                      data-testid="owner-receipt-claim-edit-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 hover:border-gold/60 hover:text-gold text-white/85 text-[11px] font-semibold transition-colors"
                    >
                      <Pencil size={11} />
                      Correct OCR
                    </button>
                  ) : null}
                </div>
              </div>

              {/* R8 — manual-review banner */}
              {ai?.needs_manual_review && !editMode ? (
                <div
                  className="mb-4 flex items-start gap-3 rounded-xl border border-amber-500/35 bg-amber-500/10 p-3"
                  data-testid="owner-receipt-claim-manual-review-banner"
                >
                  <AlertTriangle size={16} className="text-amber-300 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-100/90 leading-relaxed">
                    <span className="font-semibold">AI needs manual verification.</span>{' '}
                    {(ai.manual_review_reasons ?? []).map(humanizeMrReason).join(' · ') || 'Dedup was skipped — please correct the receipt number before approving.'}
                  </div>
                </div>
              ) : null}

              {/* R8 — dedup warning banner */}
              {claim.dedup_warning ? (
                <div
                  className="mb-4 flex items-start gap-3 rounded-xl border border-red-500/35 bg-red-500/10 p-3"
                  data-testid="owner-receipt-claim-dedup-warning"
                >
                  <AlertTriangle size={16} className="text-red-300 mt-0.5 shrink-0" />
                  <div className="text-xs text-red-100/90 leading-relaxed">
                    <span className="font-semibold">
                      Corrected receipt matches an already-approved claim.
                    </span>{' '}
                    Matching claim id:{' '}
                    <button
                      type="button"
                      onClick={() => navigate(`/owner/receipt-claims/${claim.dedup_warning.matching_claim_id}`)}
                      className="font-mono text-red-200 hover:text-red-100 underline underline-offset-2"
                      data-testid="owner-receipt-claim-dup-link"
                    >
                      {claim.dedup_warning.matching_claim_id.slice(0, 12)}…
                    </button>
                    . Reopen is blocked.
                  </div>
                </div>
              ) : null}

              {editMode ? (
                <EditAiPanel
                  values={editValues}
                  onChange={setEditValues}
                  onSave={saveEdit}
                  onCancel={cancelEdit}
                  pending={correctMut.isPending}
                />
              ) : (
                <>
                  <div className="space-y-0 divide-y divide-white/5">
                    <AiFieldRow label="Brand" value={ai?.brand_name ?? '—'} />
                    <AiFieldRow label="Outlet" value={ai?.outlet ?? '—'} />
                    <AiFieldRow label="Cashier" value={ai?.cashier ?? '—'} />
                    <AiFieldRow
                      label="Order Type"
                      value={<OrderTypePill value={ai?.order_type} />}
                      raw
                    />
                    <AiFieldRow
                      label="Table"
                      value={ai?.order_type === 'TAKEAWAY' ? '—' : (ai?.table ?? '—')}
                    />
                    <AiFieldRow label="Date" value={ai?.date ?? '—'} />
                    <AiFieldRow
                      label="Receipt No."
                      value={ai?.receipt_no ?? '—'}
                      mono
                      wrap
                      testID="owner-receipt-claim-receipt-no"
                    />
                    <AiFieldRow
                      label="Total Amount"
                      value={ai?.total_amount != null ? formatRupiah(ai.total_amount) : '—'}
                      strong
                    />
                    <AiFieldRow label="Model" value={ai?.model_used ?? '—'} mono />
                  </div>

                  {items.length > 0 ? (
                    <div className="mt-5" data-testid="owner-receipt-claim-items">
                      <h4 className="text-xs uppercase tracking-widest text-white/55 font-semibold mb-2.5">
                        Items ({items.length})
                      </h4>
                      <ul className="space-y-1.5">
                        {items.map((it, idx) => {
                          const name = (it?.name || 'Unknown item').trim();
                          const qty = it?.qty && it.qty > 1 ? ` (${it.qty})` : '';
                          const price = it?.price != null ? formatRupiah(it.price) : '';
                          return (
                            <li
                              key={idx}
                              className="flex items-start justify-between gap-3 text-sm py-1"
                              data-testid={`owner-receipt-claim-item-${idx}`}
                            >
                              <span className="text-white/85 flex-1 min-w-0">
                                <span className="text-gold mr-1.5">•</span>
                                {name}{qty}
                              </span>
                              <span className="text-white/60 font-mono shrink-0">
                                {price}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </>
              )}
            </SectionCard>
          )}

          {/* Decision panel */}
          {claim.owner_decision ? (
            <DecisionPanel decision={claim.owner_decision} />
          ) : null}

          {claim.awarded_transaction_id ? (
            <SectionCard testID="owner-receipt-claim-awarded">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-400 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-white">
                    +1 stamp awarded through loyalty engine
                  </div>
                  <div className="text-xs text-white/55 mt-1 font-mono break-all">
                    Transaction: {claim.awarded_transaction_id}
                  </div>
                </div>
              </div>
            </SectionCard>
          ) : null}

          {/* R8 — Reopen CTA for rejected + unique claims */}
          {claim.status === 'rejected' && claim.can_reopen ? (
            <SectionCard testID="owner-receipt-claim-reopen-card">
              <div className="flex items-start gap-3">
                <RotateCcw size={18} className="text-emerald-300 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-white text-sm">
                    This claim can be reopened for review
                  </div>
                  <div className="text-xs text-white/55 mt-1">
                    The corrected receipt number is unique. Reopening moves the
                    claim back to <span className="text-gold">Pending Review</span>{' '}
                    — no stamp is awarded by reopening.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => reopenMut.mutate()}
                  disabled={reopenMut.isPending}
                  data-testid="owner-receipt-claim-reopen-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gold text-black text-sm font-semibold hover:bg-gold-light disabled:opacity-50 transition-colors shrink-0 min-h-[40px]"
                >
                  {reopenMut.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RotateCcw size={14} />
                  )}
                  Reopen for Review
                </button>
              </div>
            </SectionCard>
          ) : null}

          {/* R8 — Correction History */}
          {(claim.corrections?.length ?? 0) > 0 || claim.original_ai_extraction ? (
            <CorrectionHistoryCard
              corrections={claim.corrections ?? []}
              originalAi={claim.original_ai_extraction}
              open={historyOpen}
              onToggle={() => setHistoryOpen((v) => !v)}
            />
          ) : null}

          {/* Phase R10.4 — side-by-side duplicate comparison + decision
              buttons. Only renders when the flag has a matching claim. */}
          {dedupMatches.length > 0 && dedupFlagKey !== 'green' ? (
            <div ref={comparisonRef} className="space-y-4 scroll-mt-6">
              <DuplicateComparisonPanel
                claim={claim}
                matchFetcher={(id) => api.ownerGetReceiptClaim(token, id)}
                branches={branches}
                token={token}
                apiBase={API_BASE}
              />
              {isCorrectableRole && !isAnalyzing && !editMode &&
               claim?.status === 'pending_owner_review' ? (
                <DuplicateDecisionButtons
                  claim={claim}
                  pendingDifferent={confirmDifferentMut.isPending}
                  pendingDuplicate={confirmDuplicateMut.isPending}
                  onConfirmedDifferent={(reason) =>
                    confirmDifferentMut.mutate(reason)
                  }
                  onConfirmedDuplicate={(reason) =>
                    confirmDuplicateMut.mutate(reason)
                  }
                />
              ) : null}
            </div>
          ) : null}

          {/* Action row — Phase R9 (2026-07-03): Approve CTA now also
              visible on `rejected` claims (with distinct "Approve Anyway"
              label). Reject button stays gated to `pending_owner_review`.
              Phase R10.4 (2026-07-05): Approve respects dedup_flag
              (disabled on ORANGE unless confirmed_different, RED always). */}
          {(canApproveNow || canRejectNow) ? (
            <div
              className="flex flex-col sm:flex-row gap-3"
              data-testid="owner-receipt-claim-actions"
            >
              {canRejectNow ? (
                <button
                  type="button"
                  onClick={() => setRejectOpen(true)}
                  disabled={approveMut.isPending || rejectMut.isPending}
                  data-testid="owner-receipt-claim-reject-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-transparent border border-red-500/40 text-red-300 font-semibold rounded-2xl px-5 py-3.5 text-sm hover:bg-red-500/10 disabled:opacity-50 transition-colors min-h-[48px]"
                >
                  <XCircle size={16} />
                  Reject
                </button>
              ) : null}
              {canApproveNow ? (
                <button
                  type="button"
                  onClick={() => !dedupBlocksApprove && approveMut.mutate()}
                  disabled={
                    approveMut.isPending ||
                    rejectMut.isPending ||
                    dedupBlocksApprove
                  }
                  title={approveTooltip || undefined}
                  data-testid={approveFromRejected
                    ? "owner-receipt-claim-approve-anyway-btn"
                    : "owner-receipt-claim-approve-btn"}
                  data-approve-blocked={dedupBlocksApprove ? 'true' : undefined}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-2xl px-5 py-3.5 text-sm hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-glow-gold-sm min-h-[48px]"
                >
                  {approveMut.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={16} />
                  )}
                  {approveFromRejected
                    ? 'Approve Anyway · +1 Stamp'
                    : 'Approve · Award 1 Stamp'}
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Phase R10.4 — non-blocking yellow-flag hint for the Approve CTA. */}
          {canApproveNow && dedupFlagKey === 'yellow' && !dedupBlocksApprove ? (
            <div
              className="flex items-start gap-2 text-xs text-amber-200/90 px-1"
              data-testid="owner-receipt-claim-approve-yellow-hint"
            >
              <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-300" />
              {approveTooltip}
            </div>
          ) : null}
          {/* Phase R10.4 — blocked-Approve explanation strip. */}
          {canApproveNow && dedupBlocksApprove ? (
            <div
              className={
                'flex items-start gap-2 text-xs px-1 ' +
                (dedupFlagKey === 'red' ? 'text-red-200/90' : 'text-orange-200/90')
              }
              data-testid="owner-receipt-claim-approve-blocked-hint"
            >
              <AlertTriangle
                size={12}
                className={
                  'mt-0.5 shrink-0 ' +
                  (dedupFlagKey === 'red' ? 'text-red-300' : 'text-orange-300')
                }
              />
              {approveTooltip}
            </div>
          ) : null}
        </div>
      </div>

      {rejectOpen ? (
        <RejectModal
          onClose={() => setRejectOpen(false)}
          onSubmit={(payload) => rejectMut.mutate(payload)}
          pending={rejectMut.isPending}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------
function humanizeImageError(err) {
  // Phase R10.7b (2026-07-05) — map image fetch failures to the single
  // "Image unavailable" state's secondary line. Backend already returns
  // structured `{ detail: { code, message } }` bodies for 403/404; we
  // prefer those messages verbatim so the Owner sees the real reason.
  if (!err) return 'Could not load image.';
  const { status, code, message } = err;
  if (status === 404 || code === 'image_not_found') {
    return message || 'This receipt image is no longer available on the server.';
  }
  if (status === 403 || code === 'forbidden') {
    return message || 'You do not have permission to view this image.';
  }
  if (status === 401) {
    return 'Your session may have expired — please re-login.';
  }
  if (code === 'network_error' || status === 0) {
    return message || 'Network error — check your connection.';
  }
  return message || `Could not load image (status ${status || 'unknown'}).`;
}

function formatMissingFields(fields) {
  // Phase R9.1 (2026-07-03) — human-readable list for the 400 toast.
  const human = (f) => (f === 'total_amount' ? 'total amount' : f);
  const list = Array.isArray(fields) && fields.length > 0 ? fields : ['required fields'];
  const parts = list.map(human);
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];
}

function serializeItems(items) {
  if (!Array.isArray(items) || items.length === 0) return '';
  return items
    .map((it) => {
      const name = (it?.name || '').trim();
      const qty = it?.qty != null ? it.qty : '';
      const price = it?.price != null ? it.price : '';
      return `${name}|${qty}|${price}`;
    })
    .join('\n');
}

function parseItems(text) {
  if (!text || typeof text !== 'string') return [];
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split('|').map((p) => p.trim());
      const name = parts[0] || '';
      const qty = parts[1] ? Number(parts[1]) : null;
      const price = parts[2] ? Number(parts[2]) : null;
      return {
        name,
        qty: Number.isFinite(qty) ? Math.trunc(qty) : null,
        price: Number.isFinite(price) ? Math.trunc(price) : null,
      };
    })
    .filter((it) => it.name.length > 0);
}

function equalsItems(a, b) {
  const aa = Array.isArray(a) ? a : [];
  const bb = Array.isArray(b) ? b : [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i += 1) {
    if ((aa[i]?.name || '') !== (bb[i]?.name || '')) return false;
    if ((aa[i]?.qty ?? null) !== (bb[i]?.qty ?? null)) return false;
    if ((aa[i]?.price ?? null) !== (bb[i]?.price ?? null)) return false;
  }
  return true;
}

/**
 * Diff the current AI values with the Owner's edited values and return
 * only the fields that actually changed, keyed by the backend's
 * `ai_extraction.<field>` convention.
 */
function buildCorrectionPayload(ai, edited) {
  if (!ai || !edited) return {};
  const out = {};
  const trim = (s) => (typeof s === 'string' ? s.trim() : s);
  const emptyToNull = (s) => (typeof s === 'string' && s.trim() === '' ? null : trim(s));

  const assignIfChanged = (field, nextValue) => {
    const prev = ai[field] ?? null;
    const next = nextValue;
    const prevKey = prev == null ? '' : String(prev);
    const nextKey = next == null ? '' : String(next);
    if (prevKey !== nextKey) {
      out[`ai_extraction.${field}`] = next;
    }
  };

  assignIfChanged('brand_name', emptyToNull(edited.brand_name));
  assignIfChanged('outlet',     emptyToNull(edited.outlet));
  assignIfChanged('cashier',    emptyToNull(edited.cashier));
  assignIfChanged('order_type', emptyToNull(edited.order_type));
  assignIfChanged('table',      emptyToNull(edited.table));
  assignIfChanged('receipt_no', emptyToNull(edited.receipt_no));
  assignIfChanged('date',       emptyToNull(edited.date));

  const totalRaw = edited.total_amount;
  const totalNext =
    totalRaw === '' || totalRaw == null
      ? null
      : Number.isFinite(Number(totalRaw))
        ? Math.trunc(Number(totalRaw))
        : null;
  const totalPrev = ai.total_amount != null ? Math.trunc(ai.total_amount) : null;
  if (totalNext !== totalPrev) {
    out['ai_extraction.total_amount'] = totalNext;
  }

  const nextItems = parseItems(edited.items_text);
  if (!equalsItems(ai.items ?? [], nextItems)) {
    out['ai_extraction.items'] = nextItems;
  }

  return out;
}

function humanizeMrReason(reason) {
  if (!reason) return '';
  if (reason.startsWith('ai_returned_short_sequence_value')) {
    return 'AI returned a short sequence (likely `#001`); correct the full receipt number';
  }
  if (reason === 'receipt_no_not_found') return 'No receipt number detected';
  if (reason === 'ai_call_failed_or_unparseable') return 'AI call failed; verify fields manually';
  if (reason === 'ai_runner_crashed') return 'AI runner crashed; verify fields manually';
  return reason;
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------
function CustomerDetailsCard({ customer, customerUserId }) {
  const name = customer?.name ?? null;
  const membershipId = customer?.membership_id ?? null;
  const phone = customer?.phone ?? null;
  const email = customer?.email ?? null;

  return (
    <SectionCard testID="owner-receipt-claim-customer">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <User size={16} className="text-gold" />
          Customer Details
        </h3>
        {customerUserId ? (
          <span className="font-mono text-[10px] text-white/40">
            {customerUserId.slice(0, 8)}…
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CustomerRow icon={User}    label="Name"       value={name}         testID="customer-name" />
        <CustomerRow icon={IdCard}  label="Member ID"  value={membershipId} testID="customer-membership-id" mono />
        <CustomerRow icon={Phone}   label="Phone"      value={phone}        testID="customer-phone" mono />
        <CustomerRow icon={Mail}    label="Email"      value={email}        testID="customer-email" />
      </div>
    </SectionCard>
  );
}

function CustomerRow({ icon: Icon, label, value, testID, mono }) {
  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5"
      data-testid={`owner-receipt-claim-${testID}`}
    >
      <div className="w-8 h-8 rounded-lg bg-gold/10 border border-gold/25 grid place-items-center shrink-0">
        <Icon size={14} className="text-gold" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">
          {label}
        </div>
        <div className={`text-sm text-white truncate ${mono ? 'font-mono text-xs' : ''}`}>
          {value ?? <span className="text-white/40">—</span>}
        </div>
      </div>
    </div>
  );
}

function EditAiPanel({ values, onChange, onSave, onCancel, pending }) {
  if (!values) return null;
  const set = (k) => (e) => onChange({ ...values, [k]: e.target.value });

  return (
    <div className="space-y-3" data-testid="owner-receipt-claim-edit-panel">
      <EditField label="Brand"
        inputProps={{ value: values.brand_name,  onChange: set('brand_name'),  'data-testid': 'edit-brand-name' }} />
      <EditField label="Outlet"
        inputProps={{ value: values.outlet,      onChange: set('outlet'),      'data-testid': 'edit-outlet' }} />
      <EditField label="Cashier"
        inputProps={{ value: values.cashier,     onChange: set('cashier'),     'data-testid': 'edit-cashier' }} />
      <EditField label="Order Type" type="select"
        inputProps={{ value: values.order_type,  onChange: set('order_type'),  'data-testid': 'edit-order-type' }}
        choices={ORDER_TYPE_CHOICES} />
      <EditField label="Table"
        inputProps={{ value: values.table,       onChange: set('table'),       'data-testid': 'edit-table' }} />
      <EditField label="Date" type="date"
        inputProps={{ value: values.date,        onChange: set('date'),        'data-testid': 'edit-date' }} />
      <EditField label="Receipt No." mono
        inputProps={{ value: values.receipt_no, onChange: set('receipt_no'),
                      placeholder: 'e.g. PC7252753-12069111026001',
                      'data-testid': 'edit-receipt-no' }} />
      <EditField label="Total Amount (Rp)" type="number"
        inputProps={{ value: values.total_amount, onChange: set('total_amount'),
                      min: 0, step: 1, 'data-testid': 'edit-total-amount' }} />
      <EditField label="Items" type="textarea" hint="One line per item: name|qty|price"
        inputProps={{ value: values.items_text, onChange: set('items_text'),
                      rows: 4, 'data-testid': 'edit-items' }} />
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          data-testid="owner-receipt-claim-edit-cancel"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white/80 rounded-2xl px-5 py-3 text-sm font-medium hover:bg-white/10 disabled:opacity-50 transition-colors min-h-[44px]"
        >
          <X size={14} />
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={pending}
          data-testid="owner-receipt-claim-edit-save"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-black rounded-2xl px-5 py-3 text-sm font-semibold hover:bg-gold-light disabled:opacity-50 transition-colors min-h-[44px]"
        >
          {pending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Corrections
        </button>
      </div>
    </div>
  );
}

function EditField({ label, inputProps, type, choices, hint, mono }) {
  const common =
    'w-full bg-[#0E0E13] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 focus:outline-none ' +
    (mono ? 'font-mono text-xs' : '');

  let control;
  if (type === 'select') {
    control = (
      <select {...inputProps} className={common}>
        {choices.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
    );
  } else if (type === 'textarea') {
    control = <textarea {...inputProps} className={common + ' resize-none'} />;
  } else if (type === 'date') {
    control = <input type="date" {...inputProps} className={common} />;
  } else if (type === 'number') {
    control = <input type="number" {...inputProps} className={common + ' font-mono'} />;
  } else {
    control = <input type="text" {...inputProps} className={common} />;
  }

  return (
    <label className="block">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">
          {label}
        </span>
        {hint ? <span className="text-[10px] text-white/35">{hint}</span> : null}
      </div>
      {control}
    </label>
  );
}

function CorrectionHistoryCard({ corrections, originalAi, open, onToggle }) {
  const sorted = useMemo(
    () => [...(corrections || [])].reverse(),
    [corrections],
  );

  return (
    <SectionCard testID="owner-receipt-claim-correction-history">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="owner-receipt-claim-correction-history-toggle"
      >
        <div className="inline-flex items-center gap-2">
          <History size={16} className="text-gold" />
          <h3 className="font-display text-lg font-semibold">
            Correction History
          </h3>
          <span className="text-xs text-white/55">
            ({sorted.length} correction{sorted.length === 1 ? '' : 's'})
          </span>
        </div>
        <ChevronLeft
          size={16}
          className={`text-white/55 transition-transform ${open ? '-rotate-90' : 'rotate-180'}`}
        />
      </button>
      {open ? (
        <div className="mt-4 space-y-4">
          {sorted.length === 0 ? (
            <div className="text-sm text-white/55">No corrections recorded.</div>
          ) : (
            sorted.map((c) => (
              <CorrectionEntry key={c.id ?? c.corrected_at} entry={c} />
            ))
          )}
          {originalAi ? (
            <OriginalAiPanel original={originalAi} />
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}

function CorrectionEntry({ entry }) {
  const changes = Object.entries(entry.field_changes || {});
  const dedup = entry.dedup_result || 'unknown';
  const dedupClass = dedup === 'unique'
    ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
    : dedup.startsWith('duplicate_with_claim')
      ? 'text-red-300 bg-red-500/10 border-red-500/30'
      : 'text-white/60 bg-white/5 border-white/15';

  return (
    <div
      className="rounded-xl border border-white/8 bg-white/[0.02] p-3"
      data-testid="owner-receipt-claim-correction-entry"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[11px] text-white/60 font-mono">
          {new Date(entry.corrected_at).toLocaleString('id-ID')}
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${dedupClass}`}>
          {dedup}
        </span>
      </div>
      <div className="text-[11px] text-white/55 mb-2">
        {entry.actor_role} · {String(entry.actor_user_id).slice(0, 8)}…
      </div>
      <ul className="space-y-1">
        {changes.map(([field, change]) => (
          <li key={field} className="text-xs text-white/80 font-mono flex items-start gap-2">
            <span className="text-gold shrink-0">{field.replace(/^ai_extraction\./, '')}:</span>
            <span className="min-w-0">
              <span className="text-red-300/80 line-through break-all">{renderPrim(change.from)}</span>
              <span className="text-white/40 mx-1">→</span>
              <span className="text-emerald-300 break-all">{renderPrim(change.to)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OriginalAiPanel({ original }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl border border-white/8 bg-white/[0.02]"
      data-testid="owner-receipt-claim-original-ai"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2.5 flex items-center justify-between gap-3 text-left"
        data-testid="owner-receipt-claim-original-ai-toggle"
      >
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-white/70">
          <Undo2 size={12} />
          Original AI extraction (pre-correction)
        </span>
        <ChevronLeft size={12} className={`text-white/55 transition-transform ${open ? '-rotate-90' : 'rotate-180'}`} />
      </button>
      {open ? (
        <pre className="px-3 pb-3 text-[10px] text-white/70 font-mono whitespace-pre-wrap break-all">
          {JSON.stringify(original, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

function renderPrim(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function AiFieldRow({ label, value, strong, mono, wrap, raw, testID }) {
  const widthCls = wrap ? 'max-w-[60%] break-all' : 'max-w-[60%] truncate';
  if (raw) {
    return (
      <div className="flex items-center justify-between gap-3 py-2.5">
        <span className="text-sm text-white/60">{label}</span>
        <div className="text-right max-w-[60%]">{value}</div>
      </div>
    );
  }
  return (
    <div
      className="flex items-center justify-between gap-3 py-2.5"
      {...(testID ? { 'data-testid': testID } : {})}
    >
      <span className="text-sm text-white/60">{label}</span>
      <span
        className={
          'text-sm font-semibold text-right ' + widthCls + ' ' +
          (strong ? 'text-gold text-base' : 'text-white') +
          (mono ? ' font-mono text-xs' : '')
        }
      >
        {value}
      </span>
    </div>
  );
}

function OrderTypePill({ value }) {
  if (value === 'DINE_IN') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gold/15 border border-gold/40 text-gold text-[11px] font-bold uppercase tracking-wider"
        data-testid="owner-receipt-claim-order-type"
      >
        DINE IN
      </span>
    );
  }
  if (value === 'TAKEAWAY') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-400/15 border border-sky-400/40 text-sky-300 text-[11px] font-bold uppercase tracking-wider"
        data-testid="owner-receipt-claim-order-type"
      >
        TAKEAWAY
      </span>
    );
  }
  return (
    <span
      className="text-sm font-semibold text-white"
      data-testid="owner-receipt-claim-order-type"
    >
      —
    </span>
  );
}

function HeaderStatus({ status }) {
  const map = {
    pending_ai_analysis:  { variant: 'gold',  label: 'Analyzing', icon: Hourglass },
    pending_owner_review: { variant: 'gold',  label: 'Pending Review', icon: Clock },
    approved:             { variant: 'green', label: 'Approved',  icon: CheckCircle2 },
    rejected:             { variant: 'red',   label: 'Rejected',  icon: XCircle },
  };
  const m = map[status] ?? map.pending_owner_review;
  const Icon = m.icon;
  return (
    <StatusPill variant={m.variant}>
      <Icon size={12} /> {m.label}
    </StatusPill>
  );
}

function DecisionPanel({ decision }) {
  if (!decision) return null;
  if (decision.decision === 'approved') {
    return (
      <SectionCard testID="owner-receipt-claim-decision-approved">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={18} className="text-emerald-400 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-white">Approved</div>
            <div className="text-xs text-white/55 mt-1">
              Approved by {decision.owner_user_id === 'system' ? 'system' : 'owner'} on{' '}
              {new Date(decision.decided_at).toLocaleString('id-ID')}
            </div>
          </div>
        </div>
      </SectionCard>
    );
  }
  return (
    <SectionCard testID="owner-receipt-claim-decision-rejected">
      <div className="flex items-start gap-3">
        <XCircle size={18} className="text-red-400 mt-0.5" />
        <div className="text-sm flex-1">
          <div className="font-semibold text-white">Rejected</div>
          <div className="text-xs text-white/60 mt-1">
            {rejectionReasonLabel(decision.reason)}
          </div>
          {decision.notes ? (
            <p className="text-sm text-white/70 italic mt-2 border-l-2 border-red-500/40 pl-3">
              &ldquo;{decision.notes}&rdquo;
            </p>
          ) : null}
          <div className="text-xs text-white/40 mt-2 font-mono">
            Rejected on {new Date(decision.decided_at).toLocaleString('id-ID')}
            {decision.owner_user_id === 'system' ? ' · auto (duplicate)' : ''}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function RejectModal({ onClose, onSubmit, pending }) {
  const [reason, setReason] = useState('brand_mismatch');
  const [notes, setNotes] = useState('');

  const submit = () => {
    if (!reason) return;
    onSubmit({ reason, notes: notes.trim() || null });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="owner-receipt-claim-reject-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#1A1A1A] p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/35 grid place-items-center shrink-0">
            <AlertTriangle size={18} className="text-red-400" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">
              Reject this receipt claim?
            </h3>
            <p className="text-xs text-white/55 mt-1">
              The customer will see your reason on their claim status
              screen. No stamp will be awarded.
            </p>
          </div>
        </div>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">
            Reason
          </span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="owner-receipt-claim-reject-reason"
            className="mt-1.5 w-full bg-[#0E0E13] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:border-gold/50 focus:outline-none"
          >
            {REJECTION_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label} — {r.hint}
              </option>
            ))}
          </select>
        </label>

        <label className="block mt-4">
          <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">
            Notes (optional)
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            rows={3}
            placeholder="Add context the customer will see…"
            data-testid="owner-receipt-claim-reject-notes"
            className="mt-1.5 w-full bg-[#0E0E13] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 focus:outline-none resize-none"
          />
          <div className="text-[10px] text-white/35 text-right mt-1">
            {notes.length}/1000
          </div>
        </label>

        <div className="mt-5 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="px-5 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-colors min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={pending || !reason}
            data-testid="owner-receipt-claim-reject-confirm"
            className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-50 transition-colors min-h-[44px] inline-flex items-center gap-2"
          >
            {pending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
            Reject claim
          </button>
        </div>
      </div>
    </div>
  );
}
